import React, { useState } from "react";
import axios from "axios";
import SectionHeading from "./SectionHeading";
import { LANDING } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    firm: "",
    subject: "",
    message: "",
  });
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus({ state: "error", message: "Please complete required fields." });
      return;
    }
    setStatus({ state: "loading", message: "" });
    try {
      await axios.post(`${API}/contact`, form);
      setStatus({
        state: "success",
        message:
          "Thank you. Your enquiry has been received. The partnership will respond where appropriate.",
      });
      setForm({
        full_name: "",
        email: "",
        firm: "",
        subject: "",
        message: "",
      });
    } catch (err) {
      setStatus({
        state: "error",
        message:
          "We could not transmit your enquiry. Please retry, or write to ir@krprivatecapital.com.",
      });
    }
  };

  return (
    <section
      id="contact"
      data-testid={LANDING.contact}
      className="relative py-32 lg:py-44 border-t border-[var(--kr-line-soft)]"
    >
      <div className="max-w-[1480px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <SectionHeading
            index="20"
            label="Contact"
            title={
              <>
                General{" "}
                <span className="italic text-[var(--kr-gold)]">enquiries.</span>
              </>
            }
          />
          <p className="mt-8 text-[var(--kr-text-muted)] text-[17px] leading-[1.85] max-w-[50ch]">
            For general correspondence and media enquiries, please use the form.
            Investor access requests are handled separately via the Investor
            Access panel above.
          </p>

          <div className="mt-12 space-y-8">
            <div>
              <div className="font-mono-label text-[10px] text-[var(--kr-text-muted)] mb-2">
                Platform
              </div>
              <div className="font-serif-display text-[1.3rem] text-[var(--kr-text)]">
                Global Private Markets Platform
              </div>
            </div>
            <div>
              <div className="font-mono-label text-[10px] text-[var(--kr-gold)] mb-2">
                Investor Relations
              </div>
              <a
                href="mailto:ir@krprivatecapital.com"
                className="kr-link font-serif-display text-[1.3rem]"
                data-testid={LANDING.contactIrLink}
              >
                ir@krprivatecapital.com
              </a>
            </div>
            <div>
              <div className="font-mono-label text-[10px] text-[var(--kr-text-muted)] mb-2">
                Investor &amp; Strategic Capital Enquiries
              </div>
              <a
                href="mailto:investors@krprivatecapital.com"
                className="kr-link font-serif-display text-[1.3rem]"
                data-testid={LANDING.contactEmailLink}
              >
                investors@krprivatecapital.com
              </a>
            </div>
          </div>
        </div>

        <form
          onSubmit={submit}
          data-testid={LANDING.contactForm}
          className="lg:col-span-7 lg:pl-10"
        >
          <div className="grid sm:grid-cols-2 gap-x-10 gap-y-8 mb-10">
            <div>
              <label className="font-mono-label text-[10px] text-[var(--kr-text-muted)] block mb-2">
                Full Name *
              </label>
              <input
                type="text"
                required
                value={form.full_name}
                onChange={update("full_name")}
                data-testid={LANDING.contactName}
                className="kr-input"
              />
            </div>
            <div>
              <label className="font-mono-label text-[10px] text-[var(--kr-text-muted)] block mb-2">
                Email *
              </label>
              <input
                type="email"
                required
                value={form.email}
                onChange={update("email")}
                data-testid={LANDING.contactEmail}
                className="kr-input"
              />
            </div>
            <div>
              <label className="font-mono-label text-[10px] text-[var(--kr-text-muted)] block mb-2">
                Firm / Institution (Optional)
              </label>
              <input
                type="text"
                value={form.firm}
                onChange={update("firm")}
                data-testid={LANDING.contactFirm}
                className="kr-input"
              />
            </div>
            <div>
              <label className="font-mono-label text-[10px] text-[var(--kr-text-muted)] block mb-2">
                Subject (Optional)
              </label>
              <input
                type="text"
                value={form.subject}
                onChange={update("subject")}
                data-testid={LANDING.contactSubject}
                className="kr-input"
              />
            </div>
          </div>

          <div className="mb-10">
            <label className="font-mono-label text-[10px] text-[var(--kr-text-muted)] block mb-2">
              Message *
            </label>
            <textarea
              required
              rows={5}
              value={form.message}
              onChange={update("message")}
              data-testid={LANDING.contactMessage}
              className="kr-input resize-none"
            />
          </div>

          <div className="flex flex-wrap items-center gap-6">
            <button
              type="submit"
              disabled={status.state === "loading"}
              data-testid={LANDING.contactSubmit}
              className="kr-btn-primary disabled:opacity-60"
            >
              {status.state === "loading" ? "Sending…" : "Send Enquiry"}
              <span aria-hidden>→</span>
            </button>
            {status.message && (
              <p
                data-testid={LANDING.contactStatus}
                className={`text-[14px] max-w-[42ch] ${
                  status.state === "success"
                    ? "text-[var(--kr-gold)]"
                    : "text-[#d27a7a]"
                }`}
              >
                {status.message}
              </p>
            )}
          </div>
        </form>
      </div>
    </section>
  );
};

export default Contact;
